export interface OtpProblem {
  status: number | null;
  code: string;
  reason: string;
  message: string;
  requestId: string;
  retryAfterSeconds: number | null;
}

export type OtpActionKind =
  | 'retry-code'
  | 'restart-challenge'
  | 'keep-input'
  | 'block'
  | 'rate-limited'
  | 'reauthenticate'
  | 'recover';

export interface OtpAction {
  kind: OtpActionKind;
  clearCode: boolean;
  clearChallenge: boolean;
  focusCode: boolean;
  resetResendCooldown: boolean;
  retryAfterSeconds: number;
}

export interface OtpFormState {
  challengeId: string;
  code: string;
  retryAfterSeconds: number;
  blocked?: boolean;
}

export type OtpPageFlow = 'field' | 'member';

export interface OtpPageTransition {
  action: OtpAction;
  state: OtpFormState;
  effects: {
    clearSession: boolean;
    navigateToLogin: boolean;
    focusCode: boolean;
    resetResendCooldown: boolean;
    reportUnknown: boolean;
    autoResend: false;
  };
}

interface OtpErrorDetail {
  code?: unknown;
  reason?: unknown;
  message?: unknown;
  request_id?: unknown;
  retry_after_seconds?: unknown;
}

interface RequestErrorShape {
  response?: { status?: unknown; headers?: { get?: (name: string) => string | null } };
  data?: {
    error?: OtpErrorDetail;
    data?: {
      error?: OtpErrorDetail;
    };
  };
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function retrySeconds(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.ceil(parsed) : null;
}

export function parseOtpProblem(error: unknown): OtpProblem | null {
  if (!error || typeof error !== 'object') return null;
  const requestError = error as RequestErrorShape;
  const detail = requestError.data?.data?.error || requestError.data?.error;
  if (!detail || typeof detail.reason !== 'string') return null;
  const status = requestError.response?.status;
  const bodyRetryAfterSeconds = retrySeconds(detail.retry_after_seconds);
  const retryAfterSeconds =
    bodyRetryAfterSeconds !== null
      ? bodyRetryAfterSeconds
      : retrySeconds(requestError.response?.headers?.get?.('Retry-After'));

  return {
    status: typeof status === 'number' ? status : null,
    code: stringValue(detail.code),
    reason: detail.reason,
    message: stringValue(detail.message),
    requestId: stringValue(detail.request_id),
    retryAfterSeconds,
  };
}

export function otpActionFor(problem: OtpProblem): OtpAction {
  if (problem.status === 401 && problem.reason === 'reauthentication_required') {
    return {
      kind: 'reauthenticate',
      clearCode: true,
      clearChallenge: true,
      focusCode: false,
      resetResendCooldown: false,
      retryAfterSeconds: 0,
    };
  }
  if (problem.reason === 'otp_code_invalid' || problem.reason === 'invalid_challenge') {
    return {
      kind: 'retry-code',
      clearCode: true,
      clearChallenge: false,
      focusCode: true,
      resetResendCooldown: problem.reason === 'invalid_challenge',
      retryAfterSeconds: 0,
    };
  }
  if (problem.reason === 'otp_challenge_expired' || problem.reason === 'otp_challenge_unavailable') {
    return {
      kind: 'restart-challenge',
      clearCode: true,
      clearChallenge: true,
      focusCode: false,
      resetResendCooldown: true,
      retryAfterSeconds: 0,
    };
  }
  if (
    problem.reason === 'otp_verification_unavailable' ||
    problem.reason === 'agreement_acceptance_incomplete' ||
    problem.reason === 'agreement_selection_invalid'
  ) {
    return {
      kind: 'keep-input',
      clearCode: false,
      clearChallenge: false,
      focusCode: false,
      resetResendCooldown: false,
      retryAfterSeconds: 0,
    };
  }
  if (problem.reason === 'account_access_limited' || problem.reason === 'field_access_denied') {
    return {
      kind: 'block',
      clearCode: true,
      clearChallenge: true,
      focusCode: false,
      resetResendCooldown: false,
      retryAfterSeconds: 0,
    };
  }
  if (problem.reason === 'rate_limited') {
    return {
      kind: 'rate-limited',
      clearCode: false,
      clearChallenge: false,
      focusCode: false,
      resetResendCooldown: false,
      retryAfterSeconds: Math.max(0, problem.retryAfterSeconds || 0),
    };
  }

  return {
    kind: 'recover',
    clearCode: true,
    clearChallenge: true,
    focusCode: false,
    resetResendCooldown: true,
    retryAfterSeconds: 0,
  };
}

export function applyOtpAction(state: OtpFormState, action: OtpAction): OtpFormState {
  const blocked = state.blocked === true || action.kind === 'block';
  return {
    challengeId: action.clearChallenge ? '' : state.challengeId,
    code: action.clearCode ? '' : state.code,
    retryAfterSeconds: action.kind === 'rate-limited' ? action.retryAfterSeconds : 0,
    ...(blocked ? { blocked: true } : {}),
  };
}

export function resolveOtpPageTransition(
  flow: OtpPageFlow,
  state: OtpFormState,
  problem: OtpProblem,
): OtpPageTransition {
  const action = otpActionFor(problem);
  const reauthenticateField = flow === 'field' && action.kind === 'reauthenticate';
  return {
    action,
    state: applyOtpAction(state, action),
    effects: {
      clearSession: reauthenticateField,
      navigateToLogin: reauthenticateField,
      focusCode: action.focusCode,
      resetResendCooldown: action.resetResendCooldown,
      reportUnknown: action.kind === 'recover' && Boolean(problem.requestId),
      autoResend: false,
    },
  };
}

export function shouldReauthenticateFieldSession(error: unknown): boolean {
  const status = (error as RequestErrorShape | undefined)?.response?.status;
  if (status !== 401) return false;
  const problem = parseOtpProblem(error);
  return problem ? otpActionFor(problem).kind === 'reauthenticate' : true;
}

export function rethrowRequestError(error: unknown): never {
  throw error;
}

export function unknownOtpLogContext(problem: OtpProblem): { reason: string; requestId: string } {
  return { reason: problem.reason, requestId: problem.requestId };
}

export function otpFeedbackMessage(problem: OtpProblem, action: OtpAction, fallback: string): string {
  return action.kind === 'recover' ? fallback : problem.message || fallback;
}
